import { HUMAN_AMD_RESULTS, LIVE_CALL_STATUSES } from '@/services/dialer/dialRules';

/**
 * The lines column (spec §4.1, §4.7, §6): one card per ParallelCall of the
 * session, from the server rows the dialer already polls (activeCalls).
 */
export type LineState = 'dialing' | 'ringing' | 'connected' | 'voicemail' | 'no_answer' | 'failed' | 'cut' | 'unknown';

export interface CallLineView {
  parallelCallId: string;
  displayName: string;
  phone: string;
  state: LineState;
  since: number;
  amd: 'human' | 'machine' | 'pending' | null;
  /** A second human pick-up the engine hung up: call back first (spec §4.4). */
  callBackFirst: boolean;
}

interface CallRow {
  id?: string;
  prospect_name?: string | null;
  prospect_phone?: string | null;
  call_status?: string | null;
  call_outcome?: string | null;
  amd_result?: string | null;
  connected_at?: string | null;
  agent_bridged_at?: string | null;
  started_at?: string | null;
  created_date?: string | null;
  updated_date?: string | null;
}

const MACHINE = ['machine_start', 'machine_end_beep', 'machine_end_silence', 'machine_end_other', 'fax', 'voicemail', 'machine'];
/** Ten minutes without provider activity: shown as « état inconnu » (ENG-3). */
export const LINE_STALE_MS = 10 * 60 * 1000;

/** A person was reached on this call: bridged to the caller, a person verdict, or answered. */
export function reachedPerson(row: CallRow): boolean {
  return !!row.agent_bridged_at || !!row.connected_at || HUMAN_AMD_RESULTS.includes(row.amd_result ?? '')
    || row.call_status === 'connected_to_agent';
}

/**
 * Outcomes only a person chooses (server: CallReconciliation
 * HUMAN_ONLY_OUTCOMES): a call carrying one was qualified, also on the old page.
 */
export const HUMAN_ONLY_OUTCOMES = [
  'callback_requested', 'interested', 'not_interested', 'gatekeeper', 'do_not_call', 'meeting_booked',
];

export function lineStateOf(row: CallRow, now: number): LineState {
  const status = row.call_status ?? '';
  const outcome = row.call_outcome ?? '';
  const updated = Date.parse(row.updated_date ?? row.created_date ?? '');
  const live = LIVE_CALL_STATUSES.includes(status);
  if (live && !Number.isNaN(updated) && now - updated > LINE_STALE_MS) return 'unknown';
  if (status === 'connected_to_agent' || status === 'human_detected') return 'connected';
  if (status === 'dialing') return 'dialing';
  if (status === 'ringing' || status === 'ai_analyzing') return 'ringing';
  if (status === 'voicemail' || outcome === 'voicemail' || outcome === 'left_voicemail') return 'voicemail';
  if (status === 'failed' || status === 'busy' || outcome === 'failed') return 'failed';
  if (status === 'no_answer' || outcome === 'no_answer' || outcome === 'not_reachable') return 'no_answer';
  if (status === 'completed') return 'cut';
  return 'unknown';
}

function amdOf(row: CallRow): CallLineView['amd'] {
  const r = row.amd_result ?? '';
  if (HUMAN_AMD_RESULTS.includes(r)) return 'human';
  if (MACHINE.includes(r)) return 'machine';
  return row.call_status === 'ai_analyzing' ? 'pending' : null;
}

export function toLineViews(rows: CallRow[], audibleId: string | null, now: number): CallLineView[] {
  return rows
    .filter((r) => !!r.id)
    .map((r) => {
      const state = lineStateOf(r, now);
      const since = Date.parse(r.started_at ?? r.created_date ?? '') || now;
      // A person answered (bridge, verdict) but this is not the audible
      // call and it is over: the engine hung it up after another pick-up,
      // writing « interrupted » (« <name> pris en premier »).
      const callBackFirst = r.id !== audibleId && state === 'cut' && reachedPerson(r)
        && (!r.call_outcome || r.call_outcome === 'interrupted');
      return {
        parallelCallId: r.id!,
        displayName: r.prospect_name || r.prospect_phone || '',
        phone: r.prospect_phone ?? '',
        state,
        since,
        amd: amdOf(r),
        callBackFirst,
      };
    });
}
